import apiRoot from 'helpers/apiRoot';
import { getOriginalPosition } from './sourcemaps';

const getValueOfAlfaSessionIdCookie = () => {
  const match = document.cookie.match(/(^|;\s*)alfa_session_id=([^;]+)/);
  const stringJson = match ? match[2] : '';
  return JSON.parse(decodeURIComponent(stringJson));
};

/**
 * Sends telemetry data to the ingest endpoint with proper error handling.
 */
const sendTelemetryEvent = async (event: any) => {
  try {
    const response = await fetch(`${apiRoot}/telemetry/ingest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `Telemetry request failed: ${response.status} ${response.statusText}. ${errorText}`
      );
    }
  } catch {
    // Silently fail telemetry to avoid disrupting the main application.
  }
};

/**
 * Logs HTTP request data to a telemetry endpoint.
 */
const logRequest = async (eventData: any, token: string | null) => {
  const { headers, ...restEventData } = eventData;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { Authorization, ...restHeaders } = headers || {};

  const event = {
    eventType: 'httpRequest',
    timestamp: new Date().toISOString(),
    sessionId: getValueOfAlfaSessionIdCookie().sessionId,
    token,
    pageUrl: window.location.href,
    referrer: document.referrer,
    userAgent: window.navigator.userAgent,
    eventData: {
      ...restEventData,
      headers: restHeaders,
    },
  };

  await sendTelemetryEvent(event);
};

/**
 * Captures and prepares client-side error data for logging.
 */
const captureClientError = async (error: Error, additionalContext?: any) => {
  const { filename, lineno, colno, ...restContext } = additionalContext || {};

  let sourceMapInfo = null;
  if (filename && lineno) {
    try {
      sourceMapInfo = await getOriginalPosition(filename, lineno, colno);
    } catch {
      // Ignore source map errors.
    }
  }

  const event = {
    eventType: 'clientError',
    timestamp: new Date().toISOString(),
    payload: {
      error: {
        name: error.name,
        message: error.message,
        stack: error.stack,
      },
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : '',
      additionalContext: restContext,
      sourceMap: sourceMapInfo,
    },
  };

  await sendTelemetryEvent(event);
};

/**
 * Initializes global error handlers to capture unhandled exceptions and promise rejections.
 */
const initializeErrorCapture = () => {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', event => {
    captureClientError(event.error || new Error(event.message), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', event => {
    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
    captureClientError(error, { type: 'unhandledPromiseRejection' });
  });
};

export { logRequest, captureClientError, initializeErrorCapture };
